import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash/fp';
import { useCallback } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import DisabledProductPage from 'dashboard/components/DisabledProductPage';
import { DISABLED_CASES_PAGE } from 'dashboard/components/DisabledProductPage/config';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { CaseActionBuilderName } from 'dashboard/models/case-action';
import { CaseTemplate } from 'dashboard/models/case-template';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowDeployment } from 'dashboard/models/workflow-deployment';
import { WorkflowsFilterParams } from 'dashboard/redux/actions/workflows';
import CaseActionsTable from 'dashboard/views/DashboardCaseActionsView/CaseActionsTable';
import CreateCaseActionModal from 'dashboard/views/DashboardCaseActionsView/CreateCaseActionModal';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const SearchButtonRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr minmax(300px, 1fr);
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;
export interface DashboardCaseActionsViewProps {
  isLoading: boolean;
  actionWorkflows: Workflow[];
  productFeatureIsDisabled: boolean;
  workflowDeployments: WorkflowDeployment[];
  caseTemplates: CaseTemplate[];
  filters: WorkflowsFilterParams;
  handleOmniSearch: (omniQuery: string) => void;
  handleCreateCaseAction: (builder?: string) => Promise<void>;
  archiveActionWorkflow: (actionWorkflow: Workflow) => Promise<void>;
}

const DashboardCaseActionsView = ({
  isLoading,
  productFeatureIsDisabled,
  actionWorkflows,
  caseTemplates,
  workflowDeployments,
  filters,
  handleOmniSearch,
  handleCreateCaseAction,
  archiveActionWorkflow,
}: DashboardCaseActionsViewProps) => {
  const { omni } = filters;

  const hasUsedFilters = !Object.values(filters).every(isEmpty);

  const [
    showCreateCaseActionModal,
    { enable: openCreateCaseActionModal, disable: hideCreateCaseActionModal },
  ] = useToggle();

  const handleCloseModal = useCallback(() => {
    if (!isLoading) {
      hideCreateCaseActionModal();
    }
  }, [isLoading]);

  if (productFeatureIsDisabled) {
    return (
      <DisabledProductPage
        id={DISABLED_CASES_PAGE.id}
        title={DISABLED_CASES_PAGE.title}
        headerTitle={DISABLED_CASES_PAGE.headerTitle}
        description={DISABLED_CASES_PAGE.description}
        uses={DISABLED_CASES_PAGE.uses}
        image={DISABLED_CASES_PAGE.image}
      />
    );
  }

  return (
    <>
      <Container>
        <InactiveEnvironmentBanner />
        <Header title='Case actions'>
          <HStack>
            <Permissioned permission={[Permission.CaseActionWrite, Permission.CaseActionCreate]}>
              <Button
                id='case-actions-view__create-case-action-button'
                name='create-case-action.button'
                variant='basic'
                aria-label='Create action'
                data-test='case-actions-view__create-case-action-button'
                onClick={openCreateCaseActionModal}
                disabled={isLoading}
                prefixIcon={<PlusIcon />}
              >
                Create action
              </Button>
            </Permissioned>
          </HStack>
        </Header>
        <Section data-test='case-actions-view'>
          <Card>
            <Card.Body>
              <SearchButtonRow>
                <SearchInput
                  id='case-actions-table__search-input'
                  handleInput={handleOmniSearch}
                  value={omni ?? ''}
                  placeholder='Search by name'
                  dataTest='case-actions-table__search-input'
                />
              </SearchButtonRow>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Title>Case action list</Card.Title>
              <CaseActionsTable
                actionWorkflows={actionWorkflows}
                workflowDeployments={workflowDeployments}
                caseTemplates={caseTemplates}
                hasUsedFilters={hasUsedFilters}
                isLoading={isLoading}
                archiveActionWorkflow={archiveActionWorkflow}
              />
            </Card.Body>
          </Card>
        </Section>
        <ModalWrapper
          shouldCloseOnEsc={true}
          isOpen={showCreateCaseActionModal}
          handleCloseModal={handleCloseModal}
        >
          <CreateCaseActionModal
            caseActionBuilders={Object.values(CaseActionBuilderName)}
            handleCloseModal={hideCreateCaseActionModal}
            handleCreateCaseAction={handleCreateCaseAction}
          />
        </ModalWrapper>
      </Container>
    </>
  );
};

export default DashboardCaseActionsView;
